import { Injectable, inject } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, map, take, switchMap, of } from 'rxjs';
import { UserService } from '../services/user.service';
import { IUserProfileResponseDTO } from '../domain/user';

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagGuard implements CanActivate {
  private userService = inject(UserService);
  private router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const requiredFeature = route.data['requiredFeature'] as string;
    
    if (!requiredFeature) {
      console.error('FeatureFlagGuard: No requiredFeature specified in route data');
      return of(false);
    }

    return this.userService.user$.pipe(
      take(1),
      switchMap((user: IUserProfileResponseDTO | null) => {
        // If no user in cache, fetch from server
        if (!user) {
          return this.userService.getProfile();
        }
        return of(user);
      }),
      map((user: IUserProfileResponseDTO | null) => {
        if (!user) {
          console.warn('FeatureFlagGuard: No user found, redirecting to home');
          this.router.navigate(['/']);
          return false;
        }

        const isFeatureEnabled = this.isFeatureEnabled(user, requiredFeature);
        
        if (!isFeatureEnabled) {
          console.warn(`FeatureFlagGuard: Feature '${requiredFeature}' not enabled for user, redirecting to home`);
          this.router.navigate(['/']);
          return false;
        }

        return true;
      })
    );
  }

  private isFeatureEnabled(user: IUserProfileResponseDTO, featureName: string): boolean {
    try {
      const features = user.app_metadata?.features;
      if (!features) {
        return false;
      }

      // Check if the specific feature is enabled
      return features[featureName as keyof typeof features] === true;
    } catch (error) {
      console.error('FeatureFlagGuard: Error checking feature flag', error);
      return false;
    }
  }
}
